import React, { useContext, useEffect, useRef, useState } from "react";
import { SettingsContext } from "../MathFactsGenerator/Settings/SettingsContext.tsx";
import { buildDeck } from "./deck.ts";
import { cardFaces } from "./cardFaces.ts";
import { FlashCard } from "./FlashCard.tsx";
import { useTranslation } from "../Shared/i18n/useTranslation.ts";
import { format } from "../Shared/i18n/format.ts";

/**
 * What counts as a swipe rather than a tap or a scroll: far enough, and mostly
 * sideways.
 *
 * There is deliberately no time limit. A speed threshold only rules out a slow,
 * deliberate drag - which is how a child swipes, and is no less a swipe for
 * taking a moment. Nothing else competes for the gesture: a vertical drag is
 * caught by the off-axis check and by `touch-action: pan-y`, and a tap does not
 * travel far enough.
 */
const SWIPE_MIN_DISTANCE = 50;
const SWIPE_MAX_OFF_AXIS = 0.8;

/**
 * The interactive deck: a card, a flip, and prev / next.
 *
 * The deck is generated once into state and rebuilt only on a deliberate action
 * - first mount (the useState initialiser), a settings change (operation or
 * number range), or "New deck". It is never generated during render: GenerateDataV2
 * is non-deterministic, so an inline call would reshuffle the deck on every tap.
 */
export function FlashCardDeck() {
    const { settings } = useContext(SettingsContext);
    const [deck, setDeck] = useState(() => buildDeck(settings));
    const [index, setIndex] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const t = useTranslation();

    // The first render already has a deck from the initialiser above; rebuild
    // only when the settings actually change after that.
    const firstRun = useRef(true);
    useEffect(() => {
        if (firstRun.current) {
            firstRun.current = false;
            return;
        }
        setDeck(buildDeck(settings));
        setIndex(0);
        setFlipped(false);
    }, [settings]);

    /**
     * Left and right arrows move through the deck, space flips the card.
     *
     * Bound to the document rather than to a container, so the shortcuts work
     * without hunting for something to focus first - but only outside the
     * settings panels and any text field, which have their own use for those
     * keys. A focused button is also left alone: space on "Next" should press
     * Next, which is what the browser already does.
     */
    useEffect(() => {
        function onKeyDown(event: KeyboardEvent) {
            if (event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) {
                return;
            }
            const target = event.target as HTMLElement | null;
            if (
                target?.closest?.(
                    'input, textarea, select, [contenteditable="true"], .flashCardSettings, .printableCards'
                )
            ) {
                return;
            }

            const last = deck.length - 1;
            if (event.key === "ArrowRight") {
                event.preventDefault();
                if (index < last) {
                    setIndex(index + 1);
                    setFlipped(false);
                }
            } else if (event.key === "ArrowLeft") {
                event.preventDefault();
                if (index > 0) {
                    setIndex(index - 1);
                    setFlipped(false);
                }
            } else if (event.key === " " || event.key === "Spacebar") {
                if (target?.tagName === "BUTTON") {
                    return;
                }
                // Otherwise space would scroll the page as well as flip.
                event.preventDefault();
                setFlipped((wasFlipped) => !wasFlipped);
            }
        }

        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [index, deck.length]);

    /**
     * Swipe left and right to move through the deck.
     *
     * The card is a button, so the browser follows a swipe with a click. Without
     * the `swiped` flag a swipe would turn the card over on its way past, and
     * the next card would arrive already showing its answer.
     */
    const touchStart = useRef<{ x: number; y: number } | null>(null);
    const swiped = useRef(false);

    function handleTouchStart(event: React.TouchEvent) {
        const touch = event.changedTouches[0];
        touchStart.current = { x: touch.clientX, y: touch.clientY };
        swiped.current = false;
    }

    function handleTouchEnd(event: React.TouchEvent) {
        const start = touchStart.current;
        touchStart.current = null;
        if (!start) {
            return;
        }

        const touch = event.changedTouches[0];
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;

        if (Math.abs(dx) < SWIPE_MIN_DISTANCE) return;
        // A mostly-vertical drag is the visitor scrolling the page.
        if (Math.abs(dy) > Math.abs(dx) * SWIPE_MAX_OFF_AXIS) return;

        swiped.current = true;
        goTo(dx < 0 ? index + 1 : index - 1);
    }

    /**
     * The browser takes the gesture over - a vertical pan, say - and no touchend
     * is coming. Forget the start point so the next touch is judged on its own.
     */
    function handleTouchCancel() {
        touchStart.current = null;
        swiped.current = false;
    }

    function newDeck() {
        setDeck(buildDeck(settings));
        setIndex(0);
        setFlipped(false);
    }

    function goTo(next: number) {
        if (next < 0 || next > deck.length - 1) {
            return;
        }
        setIndex(next);
        setFlipped(false);
    }

    function handleFlip() {
        // Swallow the click the browser sends after a swipe.
        if (swiped.current) {
            swiped.current = false;
            return;
        }
        setFlipped((wasFlipped) => !wasFlipped);
    }

    if (deck.length === 0) {
        return (
            <p className="flashCardEmpty">
                {t('flashCards.emptyDeck')}
            </p>
        );
    }

    const faces = cardFaces(deck[index]);

    return (
        <div
            className="flashCardDeck"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
        >
            <FlashCard faces={faces} flipped={flipped} onFlip={handleFlip} />

            <p className="flashCardProgress" aria-live="polite">
                {format(t('flashCards.cardProgress'), { current: index + 1, total: deck.length })}
            </p>

            <div className="flashCardControls">
                <button type="button" onClick={() => goTo(index - 1)} disabled={index === 0}>
                    {t('flashCards.previous')}
                </button>
                <button
                    type="button"
                    onClick={() => goTo(index + 1)}
                    disabled={index === deck.length - 1}
                >
                    {t('flashCards.next')}
                </button>
            </div>

            <button type="button" className="flashCardNewDeck" onClick={newDeck}>
                {t('flashCards.newDeck')}
            </button>

            <p className="flashCardHint">
                {t('flashCards.swipeHint')}
            </p>
        </div>
    );
}
